"use client"

import { useQuery } from "@tanstack/react-query"
import { useRouter } from "next/router"
import { useEffect, useMemo } from "react"
import { SessionData } from "./oidc-client"

const fetchSession = async (): Promise<SessionData> => {
  const response = await fetch("/api/auth/session")

  if (!response.ok) {
    throw new Error(`Failed to fetch session: ${response.statusText}`)
  }
  return (await response.json()) as SessionData
}

export function useSession() {
  const { data, isLoading, isError, error } = useQuery<SessionData, Error>({
    queryKey: ["session"],
    queryFn: fetchSession,
    suspense: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  })

  const { isLoggedIn, session } = useMemo(() => {
    // Check if iron-session data is available and user is logged in
    if (data?.isLoggedIn && data?.userInfo) {
      return {
        isLoggedIn: true,
        session: {
          userId: data.userInfo.sub,
          name: data.userInfo.name,
          email: data.userInfo.email,
          emailVerified: data.userInfo.email_verified,
          role: data.userInfo.role,
        },
      }
    }

    // Not logged in
    return {
      isLoggedIn: false,
      session: null,
    }
  }, [data?.isLoggedIn, data?.userInfo])

  return {
    data: session,
    rawSession: data,
    isLoading,
    isLoggedIn,
    isError,
    error,
    userId: session?.userId,
  }
}

export function useEnsureSession() {
  const { isLoggedIn, isLoading } = useSession()
  const router = useRouter()

  useEffect(() => {
    // Don't redirect if we're already on auth pages
    const isAuthPage = router.pathname === "/login" || router.pathname === "/signup"
    if (!isLoading && !isLoggedIn && !isAuthPage) {
      // router.push("/login")
      // Try the new auth endpoint first, fall back to old login page
      // TODO: Figure out how we're going to control where we are going to
      // redirect to....
      // router.push("/api/auth/login").catch(() => {
      //   router.push("/login")
      // })
      router.push("/api/auth/login")
    }
  }, [isLoading, isLoggedIn, router])
}

export function useEnsureUnauthenticated(redirectTo: string = "/dashboard") {
  const { isLoggedIn, isLoading } = useSession()
  const router = useRouter()

  useEffect(() => {
    if (!isLoading && isLoggedIn) {
      router.push(redirectTo)
    }
  }, [isLoading, isLoggedIn, router, redirectTo])
}

//
// These we'll eventually upgrade to decide that we should use the accounts v2 setup
//

export function getLoginRoute() {
  // This will changes to /api/auth/login later
  return "/api/auth/login"
}

export function getSignupRoute() {
  return "/signup"
}

export function useAuthenticationMechanism() {
  return {
    shouldUseAccounts: () => false,
  }
}
